import { Input, Tooltip } from "antd-v5";
import { SearchIcon } from "components/Icons";
import { WidgetEmpty } from "components/widgets/WidgetEmpty";
import { useActiveUserContext } from "contexts/ActiveUserContext";
import useDebounceCallback from "hooks/useDebounceCallback";
import useFindingColumns from "hooks/useFindingColumns";
import useTableCustomizationHelper from "hooks/useTableCustomizationHelper";
import _ from "lodash";
import React, { useCallback, useMemo, useState } from "react";
import { CorrelationsByFindingId } from "types/api/correlationsByFindingId";
import ModalUtil from "utils/modalUtil";
import { getUrlSearch } from "utils/urlUtil";

import ACButtonGroup, { ACButtonGroupItem } from "ac-components/ACButtonGroup";
import ACLink from "ac-components/ACLink/ACLink";
import ACButton from "ac-components/ac-button/ACButton";
import ACColumnVisibilityDropdown from "ac-components/ac-column-visibility-dropdown/ACColumnVisibilityDropdown";
import ColumnsIcon from "ac-components/ac-icons/ColumnsIcon";
import RedirectIcon from "ac-components/ac-icons/RedirectIcon";
import ViewFindingIcon from "ac-components/ac-icons/ViewInFindingIcon";

import AddCorrelatedFindingPopup from "../AddCorrelatedFindingPopup";
import { useFindingDetails } from "../FindingDetailsProvider";
import CorrelationTable from "./CorrelationTable";
import "./correlations.less";

function FindingIdCell({ id, findingId }: { id: string; findingId: string }) {
  const { tenantId, businessUnitId } = useActiveUserContext();
  const currentFinding = +findingId === +id;
  return (
    <div className="ac-finding-id-icon-container">
      {currentFinding ? (
        id
      ) : (
        <ACLink
          href={`#/findings/${tenantId}/${businessUnitId}/${id}`}
          target="_blank"
          className="ac-finding-id default-cursor"
        >
          {id}
        </ACLink>
      )}

      {!currentFinding ? (
        <ACButton type="outline" size="sm">
          <a
            href={`#/findings/${tenantId}/${businessUnitId}/${id}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ textDecoration: "none", color: "inherit" }}
          >
            <RedirectIcon /> New tab
          </a>
        </ACButton>
      ) : (
        <div>This finding</div>
      )}
    </div>
  );
}

const CorrelationsTab = () => {
  const {
    id,
    finding,
    findingCorrelations: _findingCorrelations,
    fetchFinding,
  } = useFindingDetails();
  const findingCorrelations = _findingCorrelations as CorrelationsByFindingId;
  const { tenantId, businessUnitId } = useActiveUserContext();
  const findingColumns = useFindingColumns({
    showFindingDetail: () => {},
    externalColumn: [],
  });

  const columns = useMemo(() => {
    if (!id) return [];

    return [
      {
        title: "ID",
        dataIndex: "id",
        key: "id",
        width: 230,
        sorter: true,
        render: (_id: string) => <FindingIdCell id={_id} findingId={id} />,
      },
      ..._.filter(
        findingColumns,
        (entry) =>
          ["title", "status", "source", "filePath", "scanType"].indexOf(
            entry.dataIndex as string,
          ) !== -1,
      ),
    ];
  }, [findingColumns, id]);

  const tableCustomizationHelper = useTableCustomizationHelper({
    columns,
    draggable: true,
    alwaysVisibleKeys: ["id", "summary"],
    defaultHiddenKeys: ["status", "source", "filePath", "scanType"],
    prefKey: "finding-correlation",
  });

  const [searchText, setSearchText] = useState<string | undefined>();

  const correlations = useMemo(() => {
    if (!findingCorrelations || findingCorrelations.length === 0) {
      return [];
    }

    if (!searchText || searchText.trim() === "") {
      return findingCorrelations;
    }

    const trimmedSearch = searchText.trim();

    // If searchText is a number, search in findingIds array
    if (!_.isNaN(+trimmedSearch)) {
      return _.filter(findingCorrelations, (correlation) =>
        _.some(correlation.findingIds, (findingId) =>
          String(findingId).includes(trimmedSearch),
        ),
      );
    }

    const lowerSearch = trimmedSearch.toLowerCase();
    return _.filter(findingCorrelations, (correlation) =>
      correlation.correlationName?.toLowerCase().includes(lowerSearch),
    );
  }, [findingCorrelations, searchText]);

  const allCorrelationIds = useMemo(
    () => _.map(correlations, (correlation) => correlation.id),
    [correlations],
  );

  const refresh = useCallback(() => {
    // Trigger a refresh of findingCorrelations by re-fetching the finding
    fetchFinding();
  }, [fetchFinding]);

  const onSearchChange = useDebounceCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchText(e.target.value);
    },
    [],
    1000,
  );

  const openAddCorrelatedFindingPopup = useCallback(() => {
    ModalUtil.show({
      content: (
        <AddCorrelatedFindingPopup
          findingId={id!}
          isV2
          correlations={correlations}
          afterClose={() => {}}
        />
      ),
      afterClose: () => {
        refresh();
      },
    });
  }, [id, refresh, correlations]);

  const searchParams = useMemo(
    () =>
      getUrlSearch({
        pagination: {
          page: 0,
        },
        groupBy: ["correlationName", "remediationAction"],
        filters: { correlationIds: allCorrelationIds },
      }),
    [allCorrelationIds],
  );

  const isFindingFoundLessThan24HoursAgo = () => {
    if (!finding) return false;
    //@ts-ignore
    return finding.foundOnDate >= Date.now() - 86400000; // calculate if finding was found before 24 hours or not
  };

  return (
    <div className="position-relative correlations-tab">
      <div className="d-flex justify-content-between align-items-center m-b-xs">
        <div>
          <Input
            prefix={<SearchIcon />}
            onChange={onSearchChange}
            allowClear
            type="search"
            placeholder="Search correlations"
          />
        </div>
        <ACButtonGroup size="md">
          <ACColumnVisibilityDropdown
            columns={columns}
            columnVisibilityConfig={
              tableCustomizationHelper.columnVisibilityConfig
            }
            onChange={tableCustomizationHelper.onColumnVisibilityChange}
            onColumnOrderChange={tableCustomizationHelper.onColumnOrderChange}
            onResetColumnOptions={tableCustomizationHelper.onResetColumnOptions}
            activeColumns={tableCustomizationHelper.exportColumns}
            alwaysVisibleKeys={tableCustomizationHelper.alwaysVisibleKeys}
          >
            <Tooltip title="Manage Columns">
              <ACButtonGroupItem id="finding-manage-columns">
                <ColumnsIcon />
              </ACButtonGroupItem>
            </Tooltip>
          </ACColumnVisibilityDropdown>

          <ACButtonGroupItem disabled={correlations.length === 0}>
            <a
              href={`/#/findings/${tenantId}/${businessUnitId}?${searchParams}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <ViewFindingIcon />
            </a>
          </ACButtonGroupItem>
          <ACButtonGroupItem onClick={openAddCorrelatedFindingPopup}>
            Add Correlation
          </ACButtonGroupItem>
        </ACButtonGroup>
      </div>
      <div className="correlations-table">
        {correlations.length > 0 ? (
          _.map(correlations, (correlation) => {
            return (
              <CorrelationTable
                refresh={refresh}
                searchText={searchText}
                key={"correlations-table-" + correlation.id}
                correlation={correlation}
                tableCustomizationHelper={tableCustomizationHelper}
              />
            );
          })
        ) : (
          <WidgetEmpty
            imgPath="/images/noproduct.png"
            icon={undefined}
            description={
              isFindingFoundLessThan24HoursAgo()
                ? `No Correlated Findings. Click “Add Manual Correlation” Above. Wait 24 hours for AI correlation`
                : `No Correlated Findings. Click “Add Manual Correlation” Above`
            }
          />
        )}
      </div>
    </div>
  );
};

export default CorrelationsTab;
